import React, { useState, useEffect, useMemo } from "react";
import { ChevronRight, ClipboardList } from "lucide-react";
import { bookings } from "../../data/bookingsData";
import { useDispatch, useSelector } from "react-redux";
import { getPastAppointment } from "../../redux/action/Appointment";
import {
  formatAppointmentType,
  formatTimeRange,
  getRiskColor,
  getStatusColor,
  getStatusText,
  getRiskTextColor,
} from "../../utils";
import { navigateTo, getHref } from "../../utils/navigation";
import NoRecent from "../../Component/EmptyRecent";
import moment from "moment";
const RecentBookedTable = () => {
  const dispatch = useDispatch();

  const { pastAppointment } = useSelector((state) => ({
    pastAppointment: state.GET_PAST_APPOINTMENT,
  }));

  const [combinedData, setCombinedData] = useState([]);

  // Fetch both past and upcoming appointment data
  const getAppointmentsData = () => {
    const pastPayload = {};

    dispatch(getPastAppointment(pastPayload, 6)); // Fetch past appointments
  };

  // Run fetch on mount
  useEffect(() => {
    getAppointmentsData();
  }, []);

  // Combine data when both past and upcoming data are available
  useEffect(() => {
    if (pastAppointment.data) {
      // Flatten the data objects (extract values from the object and combine)
      const pastData = Object.values(pastAppointment.data).flat();

      // Combine both data
      const combined = [...pastData];
      setCombinedData(combined.reverse());
    }
  }, [pastAppointment.data]);

  // Combine loading and error states
  const isLoading = pastAppointment.loading;
  const isError = pastAppointment.error;

  // const renderStatus = (item) => {
  //   console.log("item=====>", item);

  //   // return getStatusText(item.status);

  // };

  const renderStatus = (item) => {
    return getStatusText(item.status, item.currentStatus);
  };

  const renderColor = (item) => {
    return getStatusColor(item.status, item.currentStatus);
  };

  return (
    <div className="w-full md:w-2/3 bg-white border-[1px] border-solid border-[#E2E8F0] shadow-sm rounded-2xl p-[24px] min-w-0">
      {/* Header */}
      <div className="flex justify-between items-center gap-2 -mx-[24px] -mt-[24px] px-[24px] py-[14px] bg-[#E7EDF5] rounded-t-[15px]">
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center w-[36px] h-[36px] rounded-xl bg-white text-[#0D9488] flex-shrink-0">
            <ClipboardList size={18} />
          </div>
          <h2 className="text-base font-semibold text-[#171717] font-poppins mb-0">
            Recent Booked
          </h2>
        </div>
        <button
          className="flex items-center gap-1 text-[#2563EB] font-poppins text-xs font-semibold bg-white hover:bg-[#F8FAFC] rounded-full px-3 py-1.5 border-0 transition-colors"
          onClick={() =>
            navigateTo("/upcoming-appointments/upcoming")
          }
        >
          View all
          <ChevronRight size={14} />
        </button>
      </div>
      <div className="mt-4" />
      {/* Table */}
      <div className="overflow-x-auto">
        {isLoading && (
          <div className="text-center text-gray-500 mt-20">Loading...</div>
        )}
        {combinedData.length > 0 ? (
          <table className="w-full table-auto text-xs whitespace-nowrap">
            <thead>
              <tr className="bg-[#F8FAFC] text-[#64748B] font-semibold text-[11px] tracking-wide font-poppins text-left">
                <th className="px-[8px] py-[8px] rounded-l-xl">DATE</th>
                <th className="px-[8px] py-[8px]">TIME SLOT</th>
                <th className="px-[8px] py-[8px]">PATIENT NAME</th>
                <th className="px-[8px] py-[8px]">PATIENT ID</th>
                <th className="px-[8px] py-[8px]">MODE</th>
                <th className="px-[8px] py-[8px]">STATUS</th>
                <th className="px-[4px] py-[8px] text-center">RISK</th>
                <th className="px-[4px] py-[8px] rounded-r-xl"></th>
              </tr>
            </thead>
            <tbody>
              {combinedData.map((item, idx) => (
                <tr
                  key={idx}
                  className="border-b-[1px] border-solid border-x-0 border-t-0 border-[#F1F5F9] last:border-b-0 text-[#334155] hover:bg-[#F8FAFC] transition"
                >
                  <td className="px-[8px] py-[8px] text-[12px] font-poppins">
                    {item.date}
                  </td>
                  <td className="px-[8px] py-[8px] text-[12px] font-poppins">
                    {formatTimeRange(item.startTime, item.endTime)}
                  </td>
                  <td className="px-[8px] py-[8px] text-[12px] font-poppins">
                    {item.firstName} {item.lastName}
                  </td>
                  <td className="px-[8px] py-[8px] text-[12px] font-poppins">
                    {item.clientId}
                  </td>
                  <td className="px-[8px] py-[8px] text-[12px] font-poppins">
                    {formatAppointmentType(item.mode)}
                  </td>
                  <td className="px-[8px] py-[8px] whitespace-normal">
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`w-2 h-2 flex-shrink-0 rounded-full ${renderColor(item)}`}
                      ></span>
                      <span className="text-[#4B61FF] font-semibold text-[12px] leading-tight font-poppins">
                        {renderStatus(item)}
                      </span>
                    </div>
                  </td>
                  <td className="px-[4px] py-[8px] text-center">
                    <div
                      className={`inline-block px-2.5 py-0.5 text-center ${getRiskTextColor(
                        item?.riskStatus || "N/A"
                      )} rounded-full text-[11px] font-semibold font-poppins ${getRiskColor(
                        item.riskStatus
                      )}`}
                    >
                      {getStatusText(item?.riskStatus || "N/A")}
                    </div>
                  </td>
                  <td className="px-[4px]">
                    <a
                      href={getHref(
                        "/appointment-detail/" +
                        item.appointmentId +
                        "/" +
                        item.uid
                      )}
                      className="flex items-center justify-center w-[26px] h-[26px] rounded-full bg-[#F1F5F9] hover:bg-[#E2E8F0] transition-colors"
                    >
                      <ChevronRight size={14} className="text-[#64748B]" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <NoRecent />
        )}
      </div>
    </div>
  );
};

export default RecentBookedTable;
